import { LAYOUTS } from "../../data/layouts";
import { COPYRIGHT_SHORT } from "../../../utils/copyrightNotice";
import { Box, CroppedImage, Img, INK, Page, pt, Text } from "../primitives";
/**
 * «اللوحات المقترحة» page. The employee supplies images only; the frame,
 * position, border and background all come from the layout definition.
 *
 * Admin-only per page (proposalController.js keeps them for other users):
 *   page.background  - a custom background image instead of the template wall
 *   page.brandOffset - moves the logo + «ارتيكا» up/down (pt) so they never
 *                      collide with the frame
 */
const FRAME_STROKE = 3; // 6pt stroke centred on the edge, outer half clipped in the reference → 3pt inside the frame

const ASSETS = "/proposal-assets/template";

// The logo and «ارتيكا» were baked into wall-mockup.png. They are cut out of it
// pixel for pixel (wall-logo.png / wall-brand.png, same position as the
// reference) and the wall is wall-mockup-clean.png, so they can move.
export const WALL_LOGO = { x: 49.5, y: 78.75, w: 63, h: 63.75 };
export const WALL_BRAND = { x: 963.75, y: 105, w: 79.5, h: 40.5 };
export const BRAND_OFFSET_MIN = -70;
export const BRAND_OFFSET_MAX = 650;

// caption highlight - the floor grey of the wall mock-up
const CAPTION_BG = "#7c7c7e";
const CAPTION_SIZE = 28;

function resolveRect(sizing, slot) {
    if (sizing.kind === "frame")
        return sizing.rect;
    // full image at a fixed height, centred; optional zoom grows it about the centre (page edges clip it)
    const ratio = slot.image ? slot.image.width / slot.image.height : sizing.placeholderRatio;
    const zoom = slot.image ? slot.crop.zoom : 1;
    const w = Math.min(sizing.height * ratio, sizing.maxWidth) * zoom;
    const h = w / ratio;
    const baseH = h / zoom;
    return { x: sizing.centerX - w / 2, y: sizing.top - (h - baseH) * slot.crop.y, w, h };
}
function Placeholder({ index, dark }) {
    return (<div className={`doc-placeholder ${dark ? "is-dark" : ""}`}>
      <span className="doc-placeholder-label">اللوحة {index + 1}</span>
    </div>);
}
function Slot({ sizing, slot, index, dark }) {
    const rect = resolveRect(sizing, slot);
    const stroke = sizing.kind === "frame" && sizing.stroke;
    return (<>
      <Box x={rect.x} y={rect.y} w={rect.w} h={rect.h} style={{ overflow: "hidden" }}>
        {slot.image ? (sizing.kind === "frame" ? (<CroppedImage image={slot.image} crop={slot.crop} w={rect.w} h={rect.h}/>) : (<Img src={slot.image.src} x={0} y={0} w={rect.w} h={rect.h}/>)) : (<Placeholder index={index} dark={dark}/>)}
      </Box>
      {stroke && (<Box x={rect.x} y={rect.y} w={rect.w} h={rect.h} style={{ border: `${pt(FRAME_STROKE)} solid #000000`, boxSizing: "border-box" }}/>)}
    </>);
}

// Page caption (e.g. «ورق جدار») centred UNDER the frame on a grey highlight,
// so it never sits on top of the artwork
function Caption({ text, frame }) {
    return (<Text center={frame.x + frame.w / 2} top={frame.y + frame.h + 10} size={CAPTION_SIZE} weight={500} color="#ffffff" style={{
            background: CAPTION_BG,
            padding: `${pt(4)} ${pt(18)} ${pt(6)}`,
            borderRadius: pt(4),
            lineHeight: 1.3,
        }}>
      {text}
    </Text>);
}

export function SuggestedArtworkPage({ page, id }) {
    const layout = LAYOUTS[page.layout];
    const wall = layout.background === "wall";
    const offset = Number(page.brandOffset) || 0;
    const captionFrame = layout.slots[0].kind === "frame" ? layout.slots[0].rect : null;
    return (<Page background={INK} id={id}>
      {page.background ? (<Box x={0} y={0} w={1125} h={810} style={{ overflow: "hidden" }}>
          <CroppedImage image={page.background} crop={{ zoom: 1, x: 0.5, y: 0.5 }} w={1125} h={810}/>
        </Box>) : (wall && <Img src={`${ASSETS}/wall-mockup-clean.png`} x={0} y={0} w={1125} h={810}/>)}
      {wall && (<>
          <Img src={`${ASSETS}/wall-logo.png`} x={WALL_LOGO.x} y={WALL_LOGO.y + offset} w={WALL_LOGO.w} h={WALL_LOGO.h}/>
          <Img src={`${ASSETS}/wall-brand.png`} x={WALL_BRAND.x} y={WALL_BRAND.y + offset} w={WALL_BRAND.w} h={WALL_BRAND.h}/>
        </>)}
      {layout.slots.map((sizing, i) => (<Slot key={i} sizing={sizing} slot={page.slots[i] ?? { image: null, crop: { zoom: 1, x: 0.5, y: 0.5 } }} index={i} dark={!wall && !page.background}/>))}
      {layout.hasCaption && page.caption && captionFrame && <Caption text={page.caption} frame={captionFrame}/>}
      {/* ©️ حقوق التصاميم - سطر صغير بأسفل كل صفحة لوحات، خلفيته شفافة غامقة
          حتى ينقرى فوك أي خلفية (حايط، صورة، داكن) */}
      <Text center={562.5} top={788} size={8} weight={400} color="#ffffff" style={{ background: "rgba(0,0,0,0.45)", padding: `${pt(2)} ${pt(10)}`, borderRadius: pt(8) }}>
        © {COPYRIGHT_SHORT}
      </Text>
    </Page>);
}
